/* =====================================================================
 * pptxPatcher.ts — 편집된 슬라이드만 원본 OOXML에 패치하여 새 PPTX 생성
 *
 * 전략:
 * 1. JSZip으로 원본 PPTX 열기
 * 2. dirty 슬라이드만 순회하여 해당 slideN.xml의 텍스트/서식 패치
 * 3. 나머지 파트(media, theme, rels 등)는 원본 그대로 유지
 * 4. JSZip.generateAsync로 새 PPTX Blob 생성
 * ===================================================================== */

import type {
  PptxPresentation,
  PptxSlide,
  PptxShape,
  PptxTextBody,
  PptxParagraph,
  PptxRun,
  PptxFill,
  PptxOutline,
} from './pptxParser';

/* ------------------------------------------------------------------ */
/* XML 네임스페이스                                                      */
/* ------------------------------------------------------------------ */

const P_NS = 'http://schemas.openxmlformats.org/presentationml/2006/main';
const A_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';

/* ------------------------------------------------------------------ */
/* 메인 패치 함수                                                       */
/* ------------------------------------------------------------------ */

export async function patchPptx(
  pres: PptxPresentation,
  dirtySlideIds: Set<string>,
): Promise<Blob> {
  if (dirtySlideIds.size === 0) {
    return await pres.zip.generateAsync({ type: 'blob' });
  }

  for (const slide of pres.slides) {
    if (!dirtySlideIds.has(slide.id)) continue;

    const originalXml = pres.rawParts.get(slide.partPath);
    if (!originalXml) continue;

    const xmlDoc = new DOMParser().parseFromString(originalXml, 'application/xml');
    const cSld = xmlDoc.getElementsByTagNameNS(P_NS, 'cSld')[0];
    if (!cSld) continue;

    const spTree = cSld.getElementsByTagNameNS(P_NS, 'spTree')[0];
    if (!spTree) continue;

    patchShapeTree(xmlDoc, spTree, slide.shapes);

    const serializer = new XMLSerializer();
    const newXml = serializer.serializeToString(xmlDoc);
    pres.zip.file(slide.partPath, newXml);
  }

  return await pres.zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/* ------------------------------------------------------------------ */
/* ShapeTree 패치                                                       */
/* ------------------------------------------------------------------ */

function patchShapeTree(xmlDoc: Document, spTree: Element, shapes: PptxShape[]): void {
  const originalShapes: Element[] = [];
  for (let i = 0; i < spTree.childNodes.length; i++) {
    const child = spTree.childNodes[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    if (el.localName === 'sp' || el.localName === 'pic' || el.localName === 'cxnSp') {
      originalShapes.push(el);
    }
  }

  let shapeIdx = 0;
  for (const shape of shapes) {
    if (shape.type === 'group' || shape.type === 'table') {
      shapeIdx++;
      continue;
    }

    if (shapeIdx >= originalShapes.length) break;
    const originalEl = originalShapes[shapeIdx];

    if (shape.type === 'textbox' || shape.type === 'shape' || shape.type === 'connector') {
      patchShapeElement(xmlDoc, originalEl, shape);
    }

    shapeIdx++;
  }
}

/* ------------------------------------------------------------------ */
/* 개별 도형 패치                                                       */
/* ------------------------------------------------------------------ */

function patchShapeElement(xmlDoc: Document, spEl: Element, shape: PptxShape): void {
  if (shape.textBody) {
    patchTextBody(xmlDoc, spEl, shape.textBody);
  }

  const spPr = spEl.getElementsByTagNameNS(P_NS, 'spPr')[0];
  if (spPr) {
    if (shape.fill) {
      patchFill(xmlDoc, spPr, shape.fill);
    }
    if (shape.outline) {
      patchOutline(xmlDoc, spPr, shape.outline);
    }
  }
}

/* ------------------------------------------------------------------ */
/* TextBody 패치                                                        */
/* ------------------------------------------------------------------ */

function patchTextBody(xmlDoc: Document, parentEl: Element, textBody: PptxTextBody): void {
  const txBodyEl = parentEl.getElementsByTagNameNS(P_NS, 'txBody')[0];
  if (!txBodyEl) return;

  const existingParagraphs = Array.from(txBodyEl.getElementsByTagNameNS(A_NS, 'p'));

  for (let i = 0; i < textBody.paragraphs.length && i < existingParagraphs.length; i++) {
    patchParagraph(xmlDoc, existingParagraphs[i], textBody.paragraphs[i]);
  }
}

function patchParagraph(xmlDoc: Document, pEl: Element, para: PptxParagraph): void {
  const pPr = pEl.getElementsByTagNameNS(A_NS, 'pPr')[0];
  if (pPr && para.alignment) {
    pPr.setAttribute('algn', para.alignment);
  }

  const existingRuns: Element[] = [];
  for (let i = 0; i < pEl.childNodes.length; i++) {
    const child = pEl.childNodes[i];
    if (child.nodeType === 1 && (child as Element).localName === 'r' && (child as Element).namespaceURI === A_NS) {
      existingRuns.push(child as Element);
    }
  }

  const textRuns = para.runs.filter(r => r.type === 'text');

  for (let i = 0; i < textRuns.length && i < existingRuns.length; i++) {
    patchRun(xmlDoc, existingRuns[i], textRuns[i]);
  }
}

function patchRun(xmlDoc: Document, rEl: Element, run: PptxRun): void {
  const tEl = rEl.getElementsByTagNameNS(A_NS, 't')[0];
  if (tEl && run.text !== undefined) {
    tEl.textContent = run.text;
  }

  let rPr = rEl.getElementsByTagNameNS(A_NS, 'rPr')[0];
  if (!rPr) {
    rPr = xmlDoc.createElementNS(A_NS, 'a:rPr');
    rEl.insertBefore(rPr, rEl.firstChild);
  }

  if (run.bold !== undefined) rPr.setAttribute('b', run.bold ? '1' : '0');
  if (run.italic !== undefined) rPr.setAttribute('i', run.italic ? '1' : '0');
  if (run.underline !== undefined) rPr.setAttribute('u', run.underline ? 'sng' : 'none');
  if (run.strike !== undefined) rPr.setAttribute('strike', run.strike ? 'sngStrike' : 'noStrike');
  if (run.fontSize !== undefined) rPr.setAttribute('sz', String(Math.round(run.fontSize * 100)));

  if (run.color) {
    let solidFill = rPr.getElementsByTagNameNS(A_NS, 'solidFill')[0];
    if (!solidFill) {
      solidFill = xmlDoc.createElementNS(A_NS, 'a:solidFill');
      rPr.appendChild(solidFill);
    }
    while (solidFill.firstChild) solidFill.removeChild(solidFill.firstChild);

    const srgbClr = xmlDoc.createElementNS(A_NS, 'a:srgbClr');
    const colorVal = run.color.startsWith('#') ? run.color.slice(1) : run.color;
    srgbClr.setAttribute('val', colorVal);
    solidFill.appendChild(srgbClr);
  }

  if (run.fontFamily) {
    let latin = rPr.getElementsByTagNameNS(A_NS, 'latin')[0];
    if (!latin) {
      latin = xmlDoc.createElementNS(A_NS, 'a:latin');
      rPr.appendChild(latin);
    }
    latin.setAttribute('typeface', run.fontFamily);
  }
}

/* ------------------------------------------------------------------ */
/* Fill 패치                                                            */
/* ------------------------------------------------------------------ */

function patchFill(xmlDoc: Document, spPr: Element, fill: PptxFill): void {
  const existing = spPr.getElementsByTagNameNS(A_NS, 'solidFill')[0];

  if (fill.type === 'solid' && fill.color) {
    let solidFill = existing;
    if (!solidFill) {
      solidFill = xmlDoc.createElementNS(A_NS, 'a:solidFill');
      const lnEl = spPr.getElementsByTagNameNS(A_NS, 'ln')[0];
      if (lnEl) {
        spPr.insertBefore(solidFill, lnEl);
      } else {
        spPr.appendChild(solidFill);
      }
    }
    while (solidFill.firstChild) solidFill.removeChild(solidFill.firstChild);

    const srgbClr = xmlDoc.createElementNS(A_NS, 'a:srgbClr');
    const colorVal = fill.color.startsWith('#') ? fill.color.slice(1) : fill.color;
    srgbClr.setAttribute('val', colorVal);
    solidFill.appendChild(srgbClr);
  } else if (fill.type === 'none' && existing) {
    existing.parentNode?.removeChild(existing);
    const noFill = xmlDoc.createElementNS(A_NS, 'a:noFill');
    spPr.insertBefore(noFill, spPr.firstChild);
  }
}

/* ------------------------------------------------------------------ */
/* Outline 패치                                                         */
/* ------------------------------------------------------------------ */

function patchOutline(xmlDoc: Document, spPr: Element, outline: PptxOutline): void {
  let lnEl = spPr.getElementsByTagNameNS(A_NS, 'ln')[0];
  if (!lnEl) {
    lnEl = xmlDoc.createElementNS(A_NS, 'a:ln');
    spPr.appendChild(lnEl);
  }

  if (outline.width !== undefined) {
    lnEl.setAttribute('w', String(Math.round(outline.width * 12700)));
  }

  if (outline.color) {
    let solidFill = lnEl.getElementsByTagNameNS(A_NS, 'solidFill')[0];
    if (!solidFill) {
      solidFill = xmlDoc.createElementNS(A_NS, 'a:solidFill');
      lnEl.insertBefore(solidFill, lnEl.firstChild);
    }
    while (solidFill.firstChild) solidFill.removeChild(solidFill.firstChild);

    const srgbClr = xmlDoc.createElementNS(A_NS, 'a:srgbClr');
    const colorVal = outline.color.startsWith('#') ? outline.color.slice(1) : outline.color;
    srgbClr.setAttribute('val', colorVal);
    solidFill.appendChild(srgbClr);
  }
}
